import { useEffect, useState } from 'react';
import logo from '../assets/images/elayone.png';
import useNavMenu from '../hooks/useNavMenu';
import ThemeToggle from './ThemeToggle';

function useCountdown(target) {
  const calc = () => {
    if (!target) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return t;
}

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// Splits text at its first "o" so the letter can be decorated with the
// cross glyph from the reference design (e.g. "N[O]NE Urabikoze").
const splitAtFirstO = (text) => {
  if (!text) return null;
  const idx = text.search(/o/i);
  if (idx === -1) return { before: text, letter: '', after: '' };
  return { before: text.slice(0, idx), letter: text[idx], after: text.slice(idx + 1) };
};

const EventHero = ({ hero }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { links: NAV_LINKS, cta } = useNavMenu();
  const eventDate = hero.event_date ? new Date(hero.event_date) : null;
  const { days, hours, minutes, seconds } = useCountdown(eventDate);
  const elapsed = eventDate && days === 0 && hours === 0 && minutes === 0 && seconds === 0 && Date.now() >= eventDate.getTime();

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const bgUrl = hero.background_image ? `${process.env.REACT_APP_BASE_URL}${hero.background_image}` : null;
  // The title renders as two stacked lines, matching the reference: the
  // first word on its own line (with the cross-decorated O), the rest below.
  const eyebrowWords = (hero.eyebrow || '').trim().split(/\s+/).filter(Boolean);
  const titleLine1 = eyebrowWords[0] || '';
  const titleLine2 = eyebrowWords.slice(1).join(' ');
  const titleParts = splitAtFirstO(titleLine1);
  const [ticketMain, ...ticketRestArr] = (hero.badge_text || '').split(' ');
  const ticketSub = ticketRestArr.join(' ');

  const mapsHref = (hero.venue_name || hero.venue_location) && !hero.location_tbd
    ? `https://maps.google.com/?q=${encodeURIComponent([hero.venue_name, hero.venue_location].filter(Boolean).join(' '))}`
    : null;

  // Hiding this button from the homepage doesn't lock the /invite page
  // itself — it stays reachable by direct link either way.
  const showInviteButton = hero.invite_button_visible === undefined || !!hero.invite_button_visible;

  // The concert happens at a fixed moment in Kigali, so the date/time always
  // display in Africa/Kigali (UTC+2) regardless of the visitor's own timezone.
  const KIGALI_TZ = 'Africa/Kigali';
  const weekday = eventDate ? eventDate.toLocaleDateString(undefined, { weekday: 'long', timeZone: KIGALI_TZ }) : '';
  const monthShort = eventDate ? eventDate.toLocaleDateString(undefined, { month: 'short', timeZone: KIGALI_TZ }) : '';
  const dayOrdinal = eventDate ? ordinal(Number(eventDate.toLocaleDateString('en-US', { day: 'numeric', timeZone: KIGALI_TZ }))) : '';
  const hourLabel = eventDate
    ? eventDate.toLocaleTimeString(undefined, { hour: 'numeric', hour12: true, timeZone: KIGALI_TZ }).replace(':00', '')
    : '';

  return (
    <div className="ehr-hero" aria-labelledby="ehr-hero-title">
      <style>{`
        .ehr-hero{
          --u: min(1vw, 1.6vh);
          --edge: clamp(20px, 4vw, 120px);
          position: relative; min-height: 100vh; overflow: hidden; isolation: isolate;
          display: grid; grid-template-columns: minmax(0,1fr); grid-template-rows: auto auto minmax(0,1fr) auto;
          background: radial-gradient(110% 70% at 50% 0%, var(--glow) 0%, var(--paper) 55%, var(--paper-2) 100%);
          color: var(--ink); font-family: var(--cond);
        }
        .ehr-hero::before{
          content:""; position:absolute; inset:0; z-index:0; pointer-events:none; opacity:.45; mix-blend-mode:multiply;
          background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 .45  0 0 0 0 .42  0 0 0 0 .40  0 0 0 .22 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        }
        :root[data-theme="dark"] .ehr-hero::before{ opacity:.08; mix-blend-mode:screen; }
        @media (prefers-color-scheme: dark){ :root:not([data-theme="light"]) .ehr-hero::before{ opacity:.08; mix-blend-mode:screen; } }
        .ehr-ghost{
          position:absolute; z-index:1; left:50%; top:4%; width:max(115vw,120vh); max-width:none; transform:translateX(-50%);
          opacity:.06; filter:grayscale(.7) sepia(.15) blur(.8px); pointer-events:none;
          -webkit-mask-image:linear-gradient(to bottom, transparent, #000 15%, #000 38%, transparent 58%);
                  mask-image:linear-gradient(to bottom, transparent, #000 15%, #000 38%, transparent 58%);
        }
        :root[data-theme="dark"] .ehr-ghost{ opacity:.10; }
        @media (prefers-color-scheme: dark){ :root:not([data-theme="light"]) .ehr-ghost{ opacity:.10; } }

        .ehr-nav{position:relative; z-index:20; display:grid; grid-template-columns:1fr auto 1fr; align-items:center;
          padding:max(14px,calc(var(--u)*1.6)) var(--edge)}
        .ehr-nav__home{justify-self:start; display:flex}
        .ehr-nav__logo{width:max(42px,calc(var(--u)*3.6)); height:auto; filter:invert(1);}
        :root[data-theme="dark"] .ehr-nav__logo{ filter:none; }
        @media (prefers-color-scheme: dark){ :root:not([data-theme="light"]) .ehr-nav__logo{ filter:none; } }
        .ehr-nav__links{display:flex; gap:max(20px,calc(var(--u)*2.6)); list-style:none; margin:0; padding:0}
        .ehr-nav__links a{text-decoration:none; font-weight:600; font-size:max(14px,calc(var(--u)*1.05)); letter-spacing:.14em; text-transform:uppercase;
          padding:6px 0; color:var(--ink); background:linear-gradient(var(--accent),var(--accent)) left bottom/0 1.5px no-repeat; transition:background-size .25s, color .25s}
        .ehr-nav__links a:hover{background-size:100% 1.5px; color:var(--accent)}
        .ehr-nav__right{justify-self:end; display:flex; gap:max(10px,calc(var(--u)*.8)); align-items:center}
        .ehr-nav__donate{font-weight:700; font-size:max(14px,calc(var(--u)*1.05)); letter-spacing:.16em; text-transform:uppercase; color:var(--on-wine);
          background:var(--wine); text-decoration:none; border-radius:999px; padding:.7em 1.6em; transition:background .2s}
        .ehr-nav__donate:hover{background:var(--wine-deep)}
        .ehr-nav__menu{display:none; background:none; border:1.5px solid currentColor; border-radius:999px; padding:8px 15px; color:var(--ink);
          font:600 13px var(--cond); letter-spacing:.16em; text-transform:uppercase; cursor:pointer}

        .ehr-lockup{position:relative; z-index:5; text-align:center; padding:calc(var(--u)*.6) 16px 0}
        .ehr-title{font-family:var(--display); font-weight:600; margin:0; line-height:.86; letter-spacing:-.02em; text-transform:uppercase;
          font-size:calc(var(--u)*6.2); word-break:keep-all}
        .ehr-title-line{display:block; transform:scaleX(.86)}
        .ehr-title-line2{letter-spacing:-.035em}
        .ehr-o-cross{position:relative; display:inline-block}
        .ehr-o-cross svg{position:absolute; left:50%; top:50%; width:.22em; height:.62em; transform:translate(-50%,-52%); overflow:visible}
        .ehr-o-cross line{stroke:var(--accent)}
        .ehr-script{font-family:var(--script); color:var(--accent); font-size:calc(var(--u)*4); line-height:1; margin:calc(var(--u)*.2) 0 0; transform:rotate(-2deg)}
        .ehr-theme-line{display:inline-flex; align-items:center; gap:.7em; margin-top:calc(var(--u)*.9); margin-bottom: 0;
          font-weight:600; font-size:max(14px,calc(var(--u)*1.1)); letter-spacing:.14em; text-transform:uppercase; color:var(--ink-soft)}
        .ehr-dots{display:inline-flex; gap:.35em}
        .ehr-dots i{width:.62em; height:.62em; border-radius:50%; border:1.5px solid var(--ink-soft); display:inline-block}
        .ehr-dots i.on{border:.2em solid var(--accent)}
        .ehr-theme-verse{color:var(--ink); border-bottom:1.5px solid var(--accent); padding-bottom:1px}

        .ehr-cta{display:flex; justify-content:center; gap:max(10px,calc(var(--u)*1)); margin-top:calc(var(--u)*1.5)}
        .ehr-btn{display:inline-flex; align-items:center; justify-content:center; text-decoration:none; border-radius:999px;
          font-weight:700; font-size:max(14px,calc(var(--u)*1.1)); letter-spacing:.14em; text-transform:uppercase;
          padding:.85em 2.1em; line-height:1; white-space:nowrap; border:1.5px solid transparent; transition:background .2s, color .2s, border-color .2s, transform .2s}
        .ehr-btn:active{transform:translateY(1px)}
        .ehr-btn--primary{background:var(--wine); color:var(--on-wine); box-shadow:0 10px 26px -12px rgba(90,30,30,.55)}
        .ehr-btn--primary:hover{background:var(--wine-deep)}
        .ehr-btn--ghost{background:transparent; color:var(--ink); border-color:currentColor}
        .ehr-btn--ghost:hover{background:var(--accent); color:var(--on-wine); border-color:var(--accent)}
        .ehr-cta--top{display:none}

        .ehr-stage{position:relative; z-index:4; min-height:0; grid-row:3/5; grid-column:1; margin-top:0}
        .ehr-choir{position:absolute; top:0; left:calc(var(--edge)*0.35); width:calc(100% - 2*calc(var(--edge)*0.35)); height:134%; max-width:none;
          object-fit:contain; object-position:50% 100%;
          -webkit-mask-image:linear-gradient(to bottom, #000 72%, rgba(0,0,0,.55) 86%, transparent 99%);
                  mask-image:linear-gradient(to bottom, #000 72%, rgba(0,0,0,.55) 86%, transparent 99%)}
        :root[data-theme="dark"] .ehr-choir{filter:brightness(.82) saturate(.92); opacity:.93}
        @media (prefers-color-scheme: dark){ :root:not([data-theme="light"]) .ehr-choir{filter:brightness(.82) saturate(.92); opacity:.93} }

        .ehr-bottom{position:relative; z-index:6; grid-row:4; grid-column:1; padding:0 var(--edge) max(14px,calc(var(--u)*2))}
        .ehr-bottom::before{content:""; position:absolute; z-index:-1; inset:calc(var(--u)*-7) 0 0 0; pointer-events:none;
          background:linear-gradient(to bottom, rgba(var(--wash),0), rgba(var(--wash),.88) 45%, rgba(var(--wash),.98))}
        .ehr-info{display:grid; grid-template-columns:auto 1fr auto auto; grid-template-areas:"when count where ticket";
          align-items:center; column-gap:calc(var(--u)*3); max-width:calc(var(--u)*100); margin:0 auto}
        .ehr-big{font-weight:700; text-transform:uppercase; font-size:calc(var(--u)*2.5); line-height:.92; white-space:nowrap; margin:0}

        .ehr-when{grid-area:when; justify-self:start; position:relative; display:flex; align-items:center; gap:.3em;
          font-size:calc(var(--u)*2.5); padding:.5em 1.3em .55em 1.35em}
        .ehr-loop{position:absolute; inset:-4px -10px; width:calc(100% + 20px); height:calc(100% + 8px); overflow:visible}
        .ehr-loop path{stroke:var(--accent)}
        .ehr-hour{writing-mode:vertical-rl; transform:rotate(180deg); font-weight:700; font-size:.46em; line-height:1}
        .ehr-center{grid-area:count; justify-self:center; display:flex; flex-direction:column; align-items:center; gap:calc(var(--u)*1.2)}
        .ehr-cta--bar{margin:0}
        .ehr-cta--bar .ehr-btn{padding:.85em 1.8em}
        .ehr-countdown{display:flex; align-items:baseline; gap:calc(var(--u)*1.3); color:var(--ink-soft);
          padding:.55em 1.4em; border-radius:999px; font-size:max(14px,calc(var(--u)*1));
          background:rgba(var(--wash),.82); border:1px solid color-mix(in srgb, var(--ink) 14%, transparent);
          -webkit-backdrop-filter:blur(8px); backdrop-filter:blur(8px)}
        .ehr-countdown div{display:flex; align-items:baseline; gap:.3em; position:relative}
        .ehr-countdown div+div::before{content:""; position:absolute; left:calc(var(--u)*-.68); top:50%; width:3px; height:3px; border-radius:50%;
          background:var(--accent); transform:translateY(-50%)}
        .ehr-countdown b{font-family:var(--display); font-weight:600; font-size:max(18px,calc(var(--u)*1.7)); color:var(--ink); font-variant-numeric:tabular-nums}
        .ehr-countdown small{font-size:max(11px,calc(var(--u)*.85)); letter-spacing:.1em; color:var(--ink-soft)}

        .ehr-where{grid-area:where; justify-self:end; position:relative; text-decoration:none; text-align:right; padding-bottom:calc(var(--u)*2.2); color:inherit}
        .ehr-arrow{position:absolute; left:0; bottom:0; width:calc(var(--u)*8); height:auto; overflow:visible}
        .ehr-arrow path{stroke:var(--accent)}
        a.ehr-where:hover .ehr-big{color:var(--accent)}

        .ehr-ticket{grid-area:ticket; justify-self:end; display:flex; height:calc(var(--u)*5.4)}
        .ehr-ticket__face{background:var(--tan); color:var(--wine); display:flex; flex-direction:column; justify-content:center; align-items:center;
          padding:0 1.1em; text-transform:uppercase; line-height:.92; font-size:calc(var(--u)*2.3)}
        .ehr-ticket__face strong{font-weight:700}
        .ehr-ticket__face span{font-weight:600; font-size:.6em}
        .ehr-ticket__stub{width:calc(var(--u)*3.6); background:var(--wine); position:relative}
        .ehr-ticket__stub::after{content:""; position:absolute; left:28%; right:28%; top:18%; bottom:18%;
          background:repeating-linear-gradient(to bottom,#fff 0 2px,transparent 2px 4px,#fff 4px 5px,transparent 5px 8px)}

        @media (prefers-reduced-motion: no-preference){
          .ehr-ghost{animation:ehr-fade 2.2s ease-out both}
          .ehr-lockup>*{animation:ehr-rise 1s cubic-bezier(.2,.7,.2,1) both}
          .ehr-script{animation-delay:.25s!important} .ehr-theme-line{animation-delay:.4s!important} .ehr-cta{animation-delay:.55s!important}
          .ehr-choir{animation:ehr-fade 1.4s .35s ease-out both}
          .ehr-bottom{animation:ehr-rise 1s .8s cubic-bezier(.2,.7,.2,1) both}
          .ehr-o-cross line{stroke-dasharray:120; stroke-dashoffset:120; animation:ehr-draw .9s 1s ease-out forwards}
          @keyframes ehr-rise{from{opacity:0; transform:translateY(16px)}}
          @keyframes ehr-fade{from{opacity:0}}
          @keyframes ehr-draw{to{stroke-dashoffset:0}}
        }

        @media (max-width:1240px){
          .ehr-nav{grid-template-columns:auto 1fr}
          .ehr-nav__links{display:none}
          .ehr-nav__menu{display:inline-block}
          .ehr-nav.open .ehr-nav__links{display:flex; flex-direction:column; gap:2px; position:absolute; top:100%; left:16px; right:16px;
            background:var(--menu-bg); padding:12px 22px; border-top:2px solid var(--accent); box-shadow:0 18px 40px rgba(0,0,0,.18); z-index: 30;}
          .ehr-nav.open .ehr-nav__links a{display:block; padding:10px 0; font-size:16px}
        }

        @media (max-aspect-ratio: 1/1), (max-width: 640px){
          .ehr-hero{--u: min(1.9vw, 1vh)}
          .ehr-title{font-size:min(16vw,8.6vh)}
          .ehr-script{font-size:min(13vw,6.6vh)}
          .ehr-theme-line{font-size:min(3.6vw,1.9vh); margin-top:1.2vh}
          .ehr-hero{display:flex; flex-direction:column; min-height:100vh}
          .ehr-lockup{margin-top:auto}
          .ehr-bottom{margin-bottom:auto; padding-bottom:3vh}
          .ehr-stage{margin-top:0.6vh; flex:none}
          .ehr-choir{position:relative; inset:auto; width:100%; height:auto; max-height:calc(58vh - 90px); max-height:calc(58svh - 90px); object-position:50% 100%; margin-bottom:-11%;
            -webkit-mask-image:linear-gradient(to bottom, #000 78%, transparent 99%); mask-image:linear-gradient(to bottom, #000 78%, transparent 99%)}
          .ehr-bottom::before{inset:-5vh 0 -60vh 0}
          .ehr-bottom{padding:0 16px 2vh}
          .ehr-where{justify-self:center; text-align:center}
          .ehr-arrow{left:50%; transform:translateX(-50%)}
          .ehr-info{grid-template-columns:auto auto; grid-template-areas:"when ticket" "where where" "count count";
            justify-content:center; column-gap:16px; row-gap:1.6vh}
          .ehr-when,.ehr-big{font-size:min(5.6vw,3.1vh)}
          .ehr-ticket{height:min(12.5vw,6.4vh)} .ehr-ticket__face{font-size:min(5vw,2.8vh)} .ehr-ticket__stub{width:min(7vw,3.4vh)}
          .ehr-where{padding-bottom:2.6vh}
          .ehr-arrow{width:min(18vw,9vh)}
          .ehr-cta--top{display:flex}
          .ehr-cta--bar{display:none}
          .ehr-countdown{gap:16px; padding:0; background:none; border:0; -webkit-backdrop-filter:none; backdrop-filter:none}
          .ehr-countdown div+div::before{display:none}
          .ehr-countdown b{font-weight:500; font-size:min(5vw,2.8vh)}
          .ehr-countdown small{font-size:11px}
          .ehr-nav__donate{font-size:13px; padding:9px 16px}
          .ehr-cta{margin-top:1.6vh; gap:10px}
          .ehr-btn{font-size:min(3.6vw,1.8vh); padding:.95em 1.7em}
        }
      `}</style>

      {bgUrl && <img className="ehr-ghost" src={bgUrl} alt="" aria-hidden="true" loading="eager" fetchpriority="high" decoding="async" />}

      <nav className={`ehr-nav${menuOpen ? ' open' : ''}`} id="ehr-nav" aria-label="Main">
        <a className="ehr-nav__home" href="/" aria-label="Elayone Music home">
          <img className="ehr-nav__logo" src={logo} alt="Elayone Music" />
        </a>
        <ul className="ehr-nav__links" id="ehr-navlinks">
          {NAV_LINKS.map((n) => (
            <li key={n.menu_key}><a href={n.link}>{n.label}</a></li>
          ))}
        </ul>
        <div className="ehr-nav__right">
          <ThemeToggle />
          <button
            className="ehr-nav__menu"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="ehr-navlinks"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? 'Close' : 'Menu'}
          </button>
          {cta && <a className="ehr-nav__donate" href={cta.link}>{cta.label}</a>}
        </div>
      </nav>

      <header className="ehr-lockup">
        {hero.eyebrow && (
          <h1 className="ehr-title" id="ehr-hero-title">
            <span className="ehr-title-line">
              {titleParts && titleParts.letter ? (
                <>
                  {titleParts.before}
                  <span className="ehr-o-cross">
                    {titleParts.letter}
                    <svg viewBox="0 0 20 60" aria-hidden="true">
                      <line x1="10" y1="0" x2="10" y2="60" strokeWidth="4.2" />
                      <line x1="0" y1="17" x2="20" y2="17" strokeWidth="3.2" />
                    </svg>
                  </span>
                  {titleParts.after}
                </>
              ) : titleLine1}
            </span>
            {titleLine2 && <span className="ehr-title-line ehr-title-line2">{titleLine2}</span>}
          </h1>
        )}
        {hero.title && <p className="ehr-script">{hero.title}</p>}
        {hero.tagline && (
          <p className="ehr-theme-line">
            <span className="ehr-dots" aria-hidden="true"><i className="on"></i><i></i><i></i></span>
            Theme <span className="ehr-theme-verse">{hero.tagline}</span>
          </p>
        )}
        <div className="ehr-cta ehr-cta--top">
          {hero.cta_primary_label && hero.cta_primary_link && (
            <a className="ehr-btn ehr-btn--primary" href={hero.cta_primary_link}>{hero.cta_primary_label}</a>
          )}
          {showInviteButton && <a className="ehr-btn ehr-btn--ghost" href="/invite">Invite a Friend</a>}
        </div>
      </header>

      {bgUrl && (
        <div className="ehr-stage">
          <img className="ehr-choir" src={bgUrl} alt={hero.title || 'Elayone Music event'} loading="eager" fetchpriority="high" decoding="async" />
        </div>
      )}

      <footer className="ehr-bottom">
        <div className="ehr-info">
          <div className="ehr-when">
            {!hero.date_tbd && eventDate && (
              <svg className="ehr-loop" viewBox="0 0 400 120" preserveAspectRatio="none" aria-hidden="true">
                <path d="M70 14 C 230 -2, 395 18, 392 62 C 389 104, 250 118, 150 112 C 50 106, 6 88, 10 58 C 14 26, 120 10, 250 12 C 300 13, 340 16, 362 20"
                  fill="none" strokeWidth="2.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              </svg>
            )}
            {hero.date_tbd || !eventDate ? (
              <p className="ehr-big">Coming<br />Soon</p>
            ) : (
              <>
                <span className="ehr-hour">{hourLabel}</span>
                <p className="ehr-big">{weekday}<br />{dayOrdinal} {monthShort}</p>
              </>
            )}
          </div>

          <div className="ehr-center">
            <div className="ehr-cta ehr-cta--bar">
              {hero.cta_primary_label && hero.cta_primary_link && (
                <a className="ehr-btn ehr-btn--primary" href={hero.cta_primary_link}>{hero.cta_primary_label}</a>
              )}
              {showInviteButton && <a className="ehr-btn ehr-btn--ghost" href="/invite">Invite a Friend</a>}
            </div>
            {!hero.date_tbd && eventDate && (
              <div className="ehr-countdown" aria-label="Time until the concert">
                {elapsed ? (
                  <div><b>Happening today</b></div>
                ) : (
                  <>
                    <div><b>{String(days).padStart(2, '0')}</b><small>days</small></div>
                    <div><b>{String(hours).padStart(2, '0')}</b><small>hrs</small></div>
                    <div><b>{String(minutes).padStart(2, '0')}</b><small>min</small></div>
                    <div><b>{String(seconds).padStart(2, '0')}</b><small>sec</small></div>
                  </>
                )}
              </div>
            )}
          </div>

          {(hero.location_tbd || hero.venue_name) && (
            mapsHref ? (
              <a className="ehr-where" href={mapsHref} target="_blank" rel="noopener noreferrer" aria-label={`${hero.venue_name}. Open in maps`}>
                <p className="ehr-big">{hero.venue_name}{hero.venue_location ? <><br />{hero.venue_location}</> : null}</p>
                <svg className="ehr-arrow" viewBox="0 0 130 30" aria-hidden="true">
                  <path d="M4 26 C 20 10, 30 8, 34 14 C 38 20, 30 26, 42 22 L 118 8" fill="none" strokeWidth="3" strokeLinecap="round" />
                  <path d="M104 2 L 120 7 L 108 18" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            ) : (
              <div className="ehr-where">
                <p className="ehr-big">To be<br />announced</p>
              </div>
            )
          )}

          {hero.badge_text && (
            <div className="ehr-ticket" role="img" aria-label={hero.badge_text}>
              <div className="ehr-ticket__face">
                <strong>{ticketMain}</strong>
                {ticketSub && <span>{ticketSub}</span>}
              </div>
              <div className="ehr-ticket__stub"></div>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default EventHero;
